'use client';

import * as React from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { Download, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { TableCard } from '@/components/ui/table-card';
import { useUpsellsList } from '@/lib/api/upsells';
import { UpsellsTable } from './components/UpsellsTable';
import { useTablePage } from '@/hooks/use-table-page';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { upsellsQueryParamsMapping, upsellsSortMapping } from './utils/table-mappings';

const UpsellsPage = () => {
    const { t } = useTranslation('upsells');
    const { id, navigate, storeRoutes } = useStoreNavigate();
    const { setTableState, queryInput } = useTablePage({
        initialSorting: [{ id: 'createdAt', desc: true }],
        sortMapping: upsellsSortMapping,
        queryParamsMapping: upsellsQueryParamsMapping,
    });

    const {
        data: upsellsResponse,
        isLoading: loading,
        error,
        refetch,
    } = useUpsellsList(
        {
            storeId: id,
            limit: queryInput.pagination.pageSize,
            offset: (queryInput.pagination.page - 1) * queryInput.pagination.pageSize,
            sortBy: queryInput.sortBy,
        },
        {
            refetchOnMount: 'always',
            refetchOnWindowFocus: false,
        }
    );
    const upsells = upsellsResponse?.upsells || [];
    const total = upsellsResponse?.total || 0;
    return (
        <div className="flex flex-col space-y-4 sm:space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div>
                    <h1 className="text-xl font-semibold sm:text-2xl">{t('title')}</h1>
                    <p className="text-muted-foreground text-sm">{t('description')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="w-full sm:w-auto">
                        <Download className="size-4" />
                        {t('export')}
                    </Button>
                    <Button className="w-full sm:w-auto" onClick={() => navigate(storeRoutes.UPSELL_CREATE())}>
                        <Plus className="size-4" />
                        {t('createUpsell')}
                    </Button>
                </div>
            </div>
            <TableCard>
                <UpsellsTable
                    upsells={upsells}
                    total={total}
                    loading={loading}
                    onTableStateChange={setTableState}
                    onRefetch={refetch}
                />

                {error && (
                    <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                        {error instanceof Error ? error.message : t('error.loadError')}
                    </div>
                )}
            </TableCard>
        </div>
    );
};

const UpsellsPageGuarded = withRequiredPermission(UpsellsPage, PERMISSIONS.STORES_VIEW);
export default UpsellsPageGuarded;
export { UpsellsPageGuarded as UpsellsPage };
